import React from "react";
import ModalPortal from "../../components/BaseComponents/ModalPortal";
import FuxEvents from "../../lib/FuxFramework/FuxEvents";
import {NEXI_PAYMENT_LISTENER_MOUNTED} from "../../const/AppEventsCostants";
import {DLA_NEXI_PAYMENT} from "../../const/DeepLinkActionsCostants";
import ExternalRequestUtils, {
    EXTERNAL_REQUEST_MODE_DEEP_LINK
} from "../../native/ExternalRequestUtils";

const PAYMENT_PHASE_REVIEW = 'review';
const PAYMENT_PHASE_PROCESSING = 'processing';
const PAYMENT_PHASE_SUCCESS = 'success';
const PAYMENT_PHASE_FAILURE = 'failure';
const NEXI_PAYMENT_TIMEOUT_MS = 180000;
const MAX_PAYLOAD_LENGTH = 8192;
const COMPLETED_PAYMENT_STORAGE_PREFIX = 'bookizon:nexi-payment:completed:';

const NEXI_RESULT_MESSAGES = {
    '9': 'POS non connesso',
    '20': 'Pagamento annullato',
    '21': 'Pagamento rifiutato',
    '-1': 'Parametri della richiesta non validi',
    '-2': 'Il dispositivo non è considerato sicuro',
    '-3': 'Bluetooth non attivo',
    '-4': 'Servizi Nexi non disponibili',
    '-5': 'L’app Nexi deve essere aggiornata',
    '-6': 'È necessario accedere all’app Nexi',
    '-7': 'Terminale sospeso',
    '-8': 'Terminale assente',
    '-9': 'Terminale non configurato',
    '-10': 'Timeout di connessione al POS',
    '-21': 'Configurazione Nexi non valida'
};

const EXTERNAL_ERROR_MESSAGES = {
    ACTIVITY_NOT_FOUND: 'L’app Nexi POS non è installata o non può essere aperta',
    EXTERNAL_APP_RETURNED_WITHOUT_RESULT: 'L’app Nexi è stata chiusa senza restituire un risultato',
    EXTERNAL_REQUEST_TIMEOUT: 'Il pagamento non ha restituito un risultato entro il tempo previsto',
    REQUEST_CANCELLED: 'Richiesta di pagamento annullata',
    REQUEST_ALREADY_PENDING: 'È già presente un pagamento in corso',
    INVALID_EXTERNAL_REQUEST: 'Non è stato possibile avviare la richiesta di pagamento'
};

const decodeBase64UrlUtf8 = encodedPayload => {
    if (typeof encodedPayload !== 'string'
        || encodedPayload.length === 0
        || encodedPayload.length > MAX_PAYLOAD_LENGTH
        || !/^[A-Za-z0-9_-]+$/.test(encodedPayload)) {
        throw new Error('I dati della richiesta di pagamento non sono validi');
    }

    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddingLength = (4 - base64.length % 4) % 4;
    const binary = window.atob(base64 + '='.repeat(paddingLength));
    let encodedUtf8 = '';

    for (let index = 0; index < binary.length; index++) {
        encodedUtf8 += '%' + ('00' + binary.charCodeAt(index).toString(16)).slice(-2);
    }

    return decodeURIComponent(encodedUtf8);
};

const amountToCents = amount => {
    if (typeof amount !== 'number' && typeof amount !== 'string') {
        throw new Error('L’importo della richiesta non è valido');
    }

    const normalizedAmount = String(amount);
    if (!/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/.test(normalizedAmount)) {
        throw new Error('L’importo della richiesta non è valido');
    }

    const amountParts = normalizedAmount.split('.');
    const euros = parseInt(amountParts[0], 10);
    const decimals = (amountParts[1] || '').padEnd(2, '0');
    const cents = euros * 100 + parseInt(decimals || '0', 10);

    if (!Number.isSafeInteger(cents) || cents <= 0) {
        throw new Error('L’importo della richiesta deve essere maggiore di zero');
    }

    return cents;
};

export const parseNexiPaymentPayload = encodedPayload => {
    let payload = null;
    try {
        payload = JSON.parse(decodeBase64UrlUtf8(encodedPayload));
    } catch (error) {
        throw new Error('I dati della richiesta di pagamento non sono leggibili');
    }

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        throw new Error('I dati della richiesta di pagamento non sono validi');
    }

    const amountCents = amountToCents(payload.amount);
    if (typeof payload.message !== 'string' || payload.message.length > 1000) {
        throw new Error('Il messaggio della richiesta di pagamento non è valido');
    }
    if (typeof payload.transaction_id !== 'string'
        || payload.transaction_id.length === 0
        || payload.transaction_id.length > 128) {
        throw new Error('L’identificativo della transazione non è valido');
    }

    const expireAt = typeof payload.expire_at === 'string'
        ? Number(payload.expire_at)
        : payload.expire_at;
    if (!Number.isSafeInteger(expireAt) || expireAt <= 0) {
        throw new Error('La scadenza della richiesta di pagamento non è valida');
    }
    if (payload.auto_open !== undefined && payload.auto_open !== 0 && payload.auto_open !== 1) {
        throw new Error('Il parametro auto_open della richiesta di pagamento non è valido');
    }

    return {
        amountCents: amountCents,
        message: payload.message,
        transactionId: payload.transaction_id,
        expireAt: expireAt,
        autoOpen: payload.auto_open === 1
    };
};

export default class NexiPaymentListener extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            payment: null,
            phase: null,
            errorCode: null,
            errorMessage: null
        };
        this.overlayStyle = {
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 2100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            overflowY: 'auto',
            backgroundColor: 'rgba(15, 23, 42, 0.68)'
        };
        this.cardStyle = {
            width: '100%',
            maxWidth: 460,
            borderRadius: 16
        };
        this.amountStyle = {
            fontSize: 38,
            lineHeight: 1.1,
            fontWeight: 700,
            color: '#293069'
        };
        this.statusIconStyle = {
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 32,
            fontWeight: 700
        };
        this.successIconStyle = {
            ...this.statusIconStyle,
            color: '#18794e',
            backgroundColor: '#dff7e9'
        };
        this.failureIconStyle = {
            ...this.statusIconStyle,
            color: '#b42318',
            backgroundColor: '#fee4e2'
        };
    }

    componentDidMount() {
        FuxEvents.on(DLA_NEXI_PAYMENT, this.handlePaymentDeepLink);
        FuxEvents.emit(NEXI_PAYMENT_LISTENER_MOUNTED);
    }

    componentWillUnmount() {
        FuxEvents.off(DLA_NEXI_PAYMENT, this.handlePaymentDeepLink);
    }

    handlePaymentDeepLink = encodedPayload => {
        if (this.state.phase === PAYMENT_PHASE_PROCESSING) {
            return;
        }

        try {
            const payment = parseNexiPaymentPayload(encodedPayload);
            const completedPayment = this.getCompletedPayment(payment);
            if (completedPayment && completedPayment.amountCents !== payment.amountCents) {
                this.showFailure(
                    payment,
                    'DUPLICATE_TRANSACTION_MISMATCH',
                    'L’identificativo della transazione risulta già usato per un importo diverso'
                );
                return;
            }
            if (completedPayment) {
                this.setState({
                    payment: payment,
                    phase: PAYMENT_PHASE_SUCCESS,
                    errorCode: null,
                    errorMessage: null
                });
                return;
            }
            if (this.isPaymentExpired(payment)) {
                this.showFailure(payment, 'PAYMENT_REQUEST_EXPIRED', 'Questa richiesta di pagamento è scaduta, avvia di nuovo l\'acquisizione del pagamento e riprova');
                return;
            }

            this.setState({
                payment: payment,
                phase: PAYMENT_PHASE_REVIEW,
                errorCode: null,
                errorMessage: null
            }, this.handlePaymentReady);
        } catch (error) {
            this.showFailure(null, 'INVALID_PAYMENT_REQUEST', error.message);
        }
    };

    isPaymentExpired = payment => payment.expireAt <= Math.floor(Date.now() / 1000);

    handlePaymentReady = () => {
        if (this.state.payment && this.state.payment.autoOpen) {
            this.handleStartPayment();
        }
    };

    getCompletedPaymentStorageKey = payment => COMPLETED_PAYMENT_STORAGE_PREFIX + payment.transactionId;

    getCompletedPayment = payment => {
        const storageKey = this.getCompletedPaymentStorageKey(payment);
        try {
            const completedPayment = JSON.parse(localStorage.getItem(storageKey));
            if (completedPayment
                && completedPayment.expireAt > Math.floor(Date.now() / 1000)
                && Number.isSafeInteger(completedPayment.amountCents)) {
                return completedPayment;
            }
            localStorage.removeItem(storageKey);
        } catch (error) {
            localStorage.removeItem(storageKey);
        }
        return null;
    };

    markPaymentCompleted = payment => {
        localStorage.setItem(this.getCompletedPaymentStorageKey(payment), JSON.stringify({
            amountCents: payment.amountCents,
            expireAt: payment.expireAt
        }));
    };

    buildNexiPaymentUri = (payment, callbackUri) => {
        const parameters = new URLSearchParams();
        parameters.append('amount', payment.amountCents.toString());
        parameters.append('callerName', 'Bookizon');
        parameters.append('callerTrxId', payment.transactionId);
        parameters.append('uri', callbackUri);
        parameters.append('sendTicket', 'false');
        parameters.append('autoClose', 'true');
        parameters.append('hideRetry', 'false');

        return 'neximpos://payment?' + parameters.toString();
    };

    handleStartPayment = () => {
        const payment = this.state.payment;
        if (!payment || this.state.phase !== PAYMENT_PHASE_REVIEW) {
            return;
        }
        if (this.isPaymentExpired(payment)) {
            this.showFailure(payment, 'PAYMENT_REQUEST_EXPIRED', 'Questa richiesta di pagamento è scaduta');
            return;
        }

        const context = ExternalRequestUtils.createContext();
        this.setState({
            phase: PAYMENT_PHASE_PROCESSING,
            errorCode: null,
            errorMessage: null
        });

        ExternalRequestUtils.launch({
            requestId: context.requestId,
            targetUri: this.buildNexiPaymentUri(payment, context.callbackUri),
            mode: EXTERNAL_REQUEST_MODE_DEEP_LINK,
            timeoutMs: NEXI_PAYMENT_TIMEOUT_MS
        }).then(this.handlePaymentResult).catch(this.handlePaymentError);
    };

    handlePaymentResult = message => {
        const payment = this.state.payment;
        const parameters = message && message.parameters ? message.parameters : {};
        const resultCode = parameters.result === undefined ? null : String(parameters.result);

        if (resultCode !== '0') {
            const errorMessage = parameters.resultDescription
                || parameters.reason
                || parameters.message
                || NEXI_RESULT_MESSAGES[resultCode]
                || 'Nexi non ha completato il pagamento';
            const actionCode = parameters.actionCode || parameters.f39;
            const displayedCode = resultCode && actionCode
                ? resultCode + ' / ' + actionCode
                : resultCode;
            this.showFailure(payment, displayedCode || 'MISSING_NEXI_RESULT', errorMessage);
            return;
        }

        if (parameters.operationType !== 'PAYMENT'
            || parameters.callerTrxId !== payment.transactionId
            || !/^\d+$/.test(parameters.amount || '')
            || parseInt(parameters.amount, 10) !== payment.amountCents) {
            this.showFailure(
                payment,
                'INVALID_NEXI_RESULT',
                'Il risultato ricevuto da Nexi non corrisponde alla richiesta di pagamento'
            );
            return;
        }

        this.markPaymentCompleted(payment);
        this.setState({
            phase: PAYMENT_PHASE_SUCCESS,
            errorCode: null,
            errorMessage: null
        });
    };

    handlePaymentError = error => {
        const errorCode = error.code || 'PAYMENT_ERROR';
        const errorMessage = EXTERNAL_ERROR_MESSAGES[errorCode]
            || error.message
            || 'Non è stato possibile completare il pagamento';
        this.showFailure(this.state.payment, errorCode, errorMessage);
    };

    showFailure = (payment, errorCode, errorMessage) => {
        this.setState({
            payment: payment,
            phase: PAYMENT_PHASE_FAILURE,
            errorCode: errorCode,
            errorMessage: errorMessage
        });
    };

    handleDismiss = () => {
        if (this.state.phase === PAYMENT_PHASE_PROCESSING) {
            return;
        }
        this.setState({
            payment: null,
            phase: null,
            errorCode: null,
            errorMessage: null
        });
    };

    formatAmount = payment => {
        if (!payment) {
            return null;
        }
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR'
        }).format(payment.amountCents / 100);
    };

    renderReview() {
        return (
            <React.Fragment>
                <div className="text-center mb-4">
                    <div className="text-muted small text-uppercase mb-2">Richiesta di pagamento</div>
                    <div style={this.amountStyle}>{this.formatAmount(this.state.payment)}</div>
                </div>
                {
                    this.state.payment.message &&
                    <div className="alert alert-light border text-center mb-4">
                        {this.state.payment.message}
                    </div>
                }
                <button
                    className="btn btn-primary btn-lg btn-block"
                    type="button"
                    onClick={this.handleStartPayment}>
                    Avvia pagamento su Nexi
                </button>
                <button
                    className="btn btn-link btn-block text-muted mt-2"
                    type="button"
                    onClick={this.handleDismiss}>
                    Annulla
                </button>
            </React.Fragment>
        );
    }

    renderProcessing() {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary mb-3" role="status">
                    <span className="sr-only">Pagamento in corso</span>
                </div>
                <h5>Pagamento in corso</h5>
                <p className="text-muted mb-0">Completa l’operazione nell’app Nexi.</p>
            </div>
        );
    }

    renderSuccess() {
        return (
            <div className="text-center">
                <div style={this.successIconStyle}>✓</div>
                <h4>Pagamento ricevuto</h4>
                <div className="h2 text-success my-3">{this.formatAmount(this.state.payment)}</div>
                <button
                    className="btn btn-primary btn-block mt-4"
                    type="button"
                    onClick={this.handleDismiss}>
                    Chiudi
                </button>
            </div>
        );
    }

    renderFailure() {
        return (
            <div className="text-center">
                <div style={this.failureIconStyle}>!</div>
                <h4>Pagamento fallito</h4>
                {
                    this.state.payment &&
                    <div className="h3 my-3">{this.formatAmount(this.state.payment)}</div>
                }
                <p className="text-muted mb-2">{this.state.errorMessage}</p>
                {
                    this.state.errorCode &&
                    <div className="small text-muted">Codice: {this.state.errorCode}</div>
                }
                <button
                    className="btn btn-outline-primary btn-block mt-4"
                    type="button"
                    onClick={this.handleDismiss}>
                    Chiudi
                </button>
            </div>
        );
    }

    renderContent() {
        switch (this.state.phase) {
            case PAYMENT_PHASE_REVIEW:
                return this.renderReview();
            case PAYMENT_PHASE_PROCESSING:
                return this.renderProcessing();
            case PAYMENT_PHASE_SUCCESS:
                return this.renderSuccess();
            case PAYMENT_PHASE_FAILURE:
                return this.renderFailure();
            default:
                return null;
        }
    }

    render() {
        if (!this.state.phase) {
            return null;
        }

        return (
            <ModalPortal>
                <div style={this.overlayStyle}>
                    <div className="card border-0 shadow-lg" style={this.cardStyle}>
                        <div className="card-body p-4 p-sm-5">
                            {this.renderContent()}
                        </div>
                    </div>
                </div>
            </ModalPortal>
        );
    }
}
